import { css } from "@emotion/react";
import { CalendarDays } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { useStorefrontSettingsState } from "@/hooks/useStorefrontSettingsState";
import type { ImmutableObject } from "@/lib/immutable";
import type { Product } from "../../hooks/useInventoryState";
import { useProductDetailsState } from "../../hooks/useProductDetailsState";
import { useTranslate } from "../../hooks/useTranslate/useTranslate";
import { AvailabilityCalendar } from "./AvailabilityCalendar";

export type AvailabilityCalendarPopupButtonProps = {
	product: ImmutableObject<Product>;
	children: React.ReactNode;
};

export const AvailabilityCalendarPopupButton: React.FC<
	AvailabilityCalendarPopupButtonProps
> = ({ product, children }) => {
	const productDetailsState = useProductDetailsState();
	const storefrontSettingsState = useStorefrontSettingsState();
	const translate = useTranslate();
	const [open, setOpen] = useState(false);
	const showAvailabilityCalendar =
		storefrontSettingsState.get().settings?.ShowAvailabilityCalendar ?? false;

	if (!product.InventoryId || !showAvailabilityCalendar) {
		return null;
	}

	return (
		<>
			<Button
				variant="outline"
				size="sm"
				onClick={async () => {
					await productDetailsState.setProductAsync(product);
					setOpen(true);
				}}
			>
				<CalendarDays />
				{children}
				{!children && <span>{translate("Availability")}</span>}
			</Button>
			<Dialog open={open} onOpenChange={setOpen}>
				<DialogContent className="sm:max-w-5xl gap-0">
					<DialogHeader>
						<DialogTitle className="flex items-center">
							<CalendarDays size={22} className="pr-2" />
							{translate("Availability Calendar")}
						</DialogTitle>
					</DialogHeader>
					<div
						css={css`
							max-height: min(80vh, 900px);
							overflow: auto;
						`}
					>
						<AvailabilityCalendar />
					</div>
				</DialogContent>
			</Dialog>
		</>
	);
};
